import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ApiEnvelope,
  LoginResult,
  MfaSetup,
  SessionTokens,
} from 'src/app/utils/models/session.model';
import { environment } from 'src/environments/environment';

/**
 * Chiamate di sessione (login, MFA, refresh, logout).
 *
 * Usa HttpBackend e non HttpClient: queste richieste non devono passare dagli interceptor
 * (il TokenInterceptor aggiungerebbe un token scaduto e AuthenticationInterceptor riproverebbe un
 * refresh dentro un refresh) e, soprattutto, così AuthService può usarlo senza dipendenze circolari.
 */
@Injectable({ providedIn: 'root' })
export class SessionApiService {
  private readonly http: HttpClient;

  // X-Client-Auth: 2 dichiara al backend che questo client gestisce la MFA e il refresh token
  private readonly headers = new HttpHeaders({ 'X-Client-Auth': '2' });

  constructor(backend: HttpBackend) {
    this.http = new HttpClient(backend);
  }

  private url(path: string) {
    return `${environment.baseUrl}/identity/session${path}`;
  }

  private passwordUrl(path: string) {
    return `${environment.baseUrl}/identity/password${path}`;
  }

  /** Richiede il link di recupero password. La risposta è la stessa esista o no l'account. */
  forgotPassword(email: string): Observable<unknown> {
    return this.http.post(this.passwordUrl('/forgot'), { email });
  }

  resetPassword(body: {
    token: string;
    newPassword: string;
    confirmNewPassword: string;
  }): Observable<unknown> {
    return this.http.post(this.passwordUrl('/reset'), body);
  }

  login(body: {
    username: string;
    password: string;
    device_token?: string;
  }): Observable<LoginResult> {
    return this.http
      .post<ApiEnvelope<LoginResult>>(this.url(''), body, { headers: this.headers })
      .pipe(map(r => r.data));
  }

  setupMfa(mfa_token: string): Observable<MfaSetup> {
    return this.http
      .post<ApiEnvelope<MfaSetup>>(this.url('/mfa/setup'), { mfa_token }, { headers: this.headers })
      .pipe(map(r => r.data));
  }

  verifyMfa(body: {
    mfa_token: string;
    code: string;
    remember_device: boolean;
  }): Observable<SessionTokens> {
    return this.http
      .post<ApiEnvelope<SessionTokens>>(this.url('/mfa'), body, { headers: this.headers })
      .pipe(map(r => r.data));
  }

  refresh(refresh_token: string): Observable<SessionTokens> {
    return this.http
      .post<
        ApiEnvelope<SessionTokens>
      >(this.url('/refresh'), { refresh_token }, { headers: this.headers })
      .pipe(map(r => r.data));
  }

  logout(accessToken: string): Observable<unknown> {
    return this.http.post(
      this.url('/logout'),
      {},
      { headers: this.headers.set('Authorization', `Bearer ${accessToken}`) }
    );
  }
}
